import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  Mail,
  Building2,
  Phone,
  DollarSign,
  AlertTriangle,
  FileText,
  Save,
  Loader2,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { onboardingAPI, OnboardingDataset } from '../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface FormData {
  invitee_email: string;
  invitee_company_name: string;
  invitee_contact_name: string;
  invitee_phone: string;
  message: string;
  initial_risk_level: 'low' | 'medium' | 'high' | 'critical';
  estimated_annual_spend: string;
  questionnaire_datasets: number[];
  expires_days: number;
}

export default function NewInvitation() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [datasets, setDatasets] = useState<OnboardingDataset[]>([]);
  const [formData, setFormData] = useState<FormData>({
    invitee_email: '',
    invitee_company_name: '',
    invitee_contact_name: '',
    invitee_phone: '',
    message: '',
    initial_risk_level: 'medium',
    estimated_annual_spend: '',
    questionnaire_datasets: [],
    expires_days: 30,
  });

  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    try {
      const response = await onboardingAPI.getDatasets({ is_active: true });
      setDatasets(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch datasets:', error);
    }
  };

  const handleSaveDraft = async () => {
    if (!formData.invitee_email || !formData.invitee_company_name) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await onboardingAPI.createInvitation({
        ...formData,
        estimated_annual_spend: formData.estimated_annual_spend ? parseFloat(formData.estimated_annual_spend) : undefined,
      });
      toast.success('Invitation saved as draft');
      navigate('/onboarding');
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to create invitation'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!formData.invitee_email || !formData.invitee_company_name) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      // Create the invitation first
      const response = await onboardingAPI.createInvitation({
        ...formData,
        estimated_annual_spend: formData.estimated_annual_spend ? parseFloat(formData.estimated_annual_spend) : undefined,
      });

      // Then submit for approval
      if (response.data.id) {
        await onboardingAPI.submitForApproval(response.data.id);
        toast.success('Invitation submitted for approval');
      }

      navigate('/onboarding');
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to submit invitation for approval'));
    } finally {
      setSaving(false);
    }
  };

  const toggleDataset = (datasetId: number) => {
    setFormData(prev => ({
      ...prev,
      questionnaire_datasets: prev.questionnaire_datasets.includes(datasetId)
        ? prev.questionnaire_datasets.filter(id => id !== datasetId)
        : [...prev.questionnaire_datasets, datasetId],
    }));
  };

  const riskLevelColors: Record<string, string> = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-red-100 text-red-800',
    critical: 'bg-purple-100 text-purple-800',
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/onboarding')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Invite Supplier</h1>
          <p className="text-muted-foreground">
            Send an onboarding invitation to a new supplier
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="grid gap-6">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Supplier Information
            </CardTitle>
            <CardDescription>
              Enter the supplier company and contact details
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  placeholder="Enter company name"
                  value={formData.invitee_company_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, invitee_company_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  placeholder="Enter contact name"
                  value={formData.invitee_contact_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, invitee_contact_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="supplier@company.com"
                    className="pl-9"
                    value={formData.invitee_email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, invitee_email: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Enter phone number"
                    className="pl-9"
                    value={formData.invitee_phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, invitee_phone: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Assessment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Initial Assessment
            </CardTitle>
            <CardDescription>
              Set initial risk level and estimated spend
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Initial Risk Level</Label>
                <Select
                  value={formData.initial_risk_level}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, initial_risk_level: value as 'low' | 'medium' | 'high' | 'critical' }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select risk level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      <div className="flex items-center gap-2">
                        <Badge className={riskLevelColors['low']}>Low</Badge>
                        <span className="text-muted-foreground">Standard onboarding</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="medium">
                      <div className="flex items-center gap-2">
                        <Badge className={riskLevelColors['medium']}>Medium</Badge>
                        <span className="text-muted-foreground">Enhanced due diligence</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="high">
                      <div className="flex items-center gap-2">
                        <Badge className={riskLevelColors['high']}>High</Badge>
                        <span className="text-muted-foreground">Full compliance review</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="critical">
                      <div className="flex items-center gap-2">
                        <Badge className={riskLevelColors['critical']}>Critical</Badge>
                        <span className="text-muted-foreground">Executive approval required</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spend">Estimated Annual Spend</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="spend"
                    type="number"
                    placeholder="0.00"
                    className="pl-9"
                    value={formData.estimated_annual_spend}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, estimated_annual_spend: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="expires">Invitation Valid For</Label>
              <Select
                value={formData.expires_days.toString()}
                onValueChange={(value: string) => setFormData(prev => ({ ...prev, expires_days: parseInt(value) }))}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Questionnaires */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Questionnaires
            </CardTitle>
            <CardDescription>
              Select which questionnaires the supplier should complete
            </CardDescription>
          </CardHeader>
          <CardContent>
            {datasets.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No questionnaire templates available.{' '}
                <Button variant="link" className="px-0" onClick={() => navigate('/onboarding/datasets/new')}>
                  Create one
                </Button>
              </div>
            ) : (
              <div className="grid gap-3">
                {datasets.map((dataset) => (
                  <div
                    key={dataset.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.questionnaire_datasets.includes(dataset.id)
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => toggleDataset(dataset.id)}
                  >
                    <Checkbox
                      checked={formData.questionnaire_datasets.includes(dataset.id)}
                      onCheckedChange={() => toggleDataset(dataset.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{dataset.name}</span>
                        {dataset.is_mandatory && (
                          <Badge variant="secondary">Required</Badge>
                        )}
                        <Badge variant="outline">{dataset.dataset_type}</Badge>
                      </div>
                      {dataset.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {dataset.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Message */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Invitation Message
            </CardTitle>
            <CardDescription>
              Add a custom message to include in the invitation email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Enter a custom message for the supplier (optional)"
              rows={4}
              value={formData.message}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, message: e.target.value }))}
            />
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>Invitations require approval before being sent</span>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/onboarding')}>
            Cancel
          </Button>
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save as Draft
          </Button>
          <Button onClick={handleSubmitForApproval} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Submit for Approval
          </Button>
        </div>
      </div>
    </div>
  );
}
